import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { Button } from '@/components/ui/Button';
import { SupportTicket, TicketPriority, SupportTicketStatus } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// SURGICAL FIX: Import the mock data file directly.
import mockSupportData from '@/../public/support-tickets.json';

const StatusPill: React.FC<{ status: SupportTicketStatus }> = ({ status }) => {
    const statusStyle = {
        [SupportTicketStatus.OPEN]: 'bg-yellow-500/20 text-yellow-300',
        [SupportTicketStatus.IN_PROGRESS]: 'bg-blue-500/20 text-blue-300',
        [SupportTicketStatus.CLOSED]: 'bg-green-500/20 text-green-300',
    }[status] || 'bg-gray-500/20 text-gray-300';

    return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyle}`}>{status}</span>;
};

const PriorityPill: React.FC<{ priority: TicketPriority }> = ({ priority }) => {
    const priorityStyle = {
        [TicketPriority.HIGH]: 'text-red-400',
        [TicketPriority.MEDIUM]: 'text-yellow-400',
        [TicketPriority.LOW]: 'text-gray-400',
    }[priority] || 'text-gray-400';

    return <span className={`font-bold ${priorityStyle}`}>{priority}</span>;
};

interface SupportRequestsRealDataTableProps {
  forCurrentUser?: boolean;
}

const SupportRequestsRealDataTable: React.FC<SupportRequestsRealDataTableProps> = ({ forCurrentUser = false }) => {
  const [supportRequests, setSupportRequests] = useState<SupportTicket[]>([]);
  const [allSupportRequests, setAllSupportRequests] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [usingMockData, setUsingMockData] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { user } = useAuth();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async (pageNum: number, search: string) => {
    setIsLoading(true);
    setError(null);
    try {
        const params = new URLSearchParams({ page: pageNum.toString(), limit: PAGE_SIZE.toString(), search: search });
        const apiPath = forCurrentUser ? '/api/support/mine' : '/api/support';
        const url = `${API_BASE_URL}${apiPath}?${params.toString()}`;
        const response = await fetch(url, { credentials: 'include' });

        if (!response.ok) {
            throw new Error('API endpoint not found, falling back to mock data.');
        }
        
        const data = await response.json();
        setSupportRequests(data);
        setUsingMockData(false);
        addLog('Fetched support tickets from API', LogLevel.INFO, LogCategory.API);

    } catch (apiError) {
        addLog(`API fetch failed, falling back to imported mock data: ${(apiError as Error).message}`, LogLevel.WARN, LogCategory.API);
        setUsingMockData(true);
        const allData = mockSupportData.map(ticket => ({...ticket, userId: ticket.user.email, userName: ticket.user.name, category: ticket.category, tier: 'Tier 1', lastUpdate: new Date().toISOString() })) as unknown as SupportTicket[];
        
        if (forCurrentUser && user) {
            setAllSupportRequests(allData.filter(ticket => ticket.userId === user.email));
        } else {
            setAllSupportRequests(allData);
        }

        if (process.env.NODE_ENV === 'development') {
          notify('Displaying mock data. Server not reachable.', 'warn');
        }
    } finally {
        setIsLoading(false);
    }
  }, [notify, addLog, forCurrentUser, user]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (page !== 1) setPage(1);
      fetchData(usingMockData ? 1 : page, searchTerm);
    }, 500);
    return () => clearTimeout(debounce);
  }, [searchTerm, fetchData, page, usingMockData]);
  
  useEffect(() => {
      if (!usingMockData) {
          fetchData(page, searchTerm);
      }
  }, [page, searchTerm, usingMockData, fetchData]);

  const handleResolve = (id: string) => {
    setSupportRequests(prevRequests => 
      prevRequests.map(req => req.id === id ? { ...req, status: SupportTicketStatus.CLOSED } : req)
    );
    console.log(`Resolving request ${id}`);
  };

  const handleEscalate = (id: string) => {
    setSupportRequests(prevRequests => 
      prevRequests.map(req => req.id === id ? { ...req, priority: TicketPriority.HIGH } : req)
    );
    console.log(`Escalating request ${id}`);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this support ticket?')) return;

    addLog(`Initiated delete for support ticket ${id}`, LogLevel.INFO, LogCategory.SUPPORT);

    if (usingMockData) {
        setAllSupportRequests(current => current.filter(req => req.id !== id));
        notify(`(Mock) Support ticket ${id} deleted.`, 'success');
    } else {
        try {
            const response = await fetch(`${API_BASE_URL}/api/support/${id}`, { method: 'DELETE', credentials: 'include' });
            if (!response.ok) {
                throw new Error('Failed to delete support ticket from API');
            }
            fetchData(page, searchTerm); 
            notify(`Support ticket ${id} deleted successfully.`, 'success');
            addLog(`Support ticket ${id} successfully deleted.`, LogLevel.INFO, LogCategory.SUPPORT);
        } catch (err) {
            const errorMessage = (err as Error).message;
            notify(errorMessage, 'error');
            addLog(`Failed to delete support ticket ${id}: ${errorMessage}`, LogLevel.ERROR, LogCategory.SUPPORT);
        }
    }
  };

  const displayedRequests = useMemo(() => {
    if (!usingMockData) return supportRequests;
    const filtered = allSupportRequests.filter(req => 
      req.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (req.userName && req.userName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [supportRequests, allSupportRequests, page, searchTerm, usingMockData]);

  const totalFilteredCount = useMemo(() => {
    if (!usingMockData) return 0;
    return allSupportRequests.filter(req => 
        req.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (req.userName && req.userName.toLowerCase().includes(searchTerm.toLowerCase()))
    ).length;
  }, [allSupportRequests, searchTerm, usingMockData]);

  const hasMore = usingMockData
    ? page * PAGE_SIZE < totalFilteredCount
    : supportRequests.length === PAGE_SIZE;

    if (isLoading && displayedRequests.length === 0) return (
        <div className="flex justify-center items-center h-48">
            <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
            <p className="ml-4 text-kala-400">Loading support requests...</p>
        </div>
    );
    if (error && !usingMockData) return <div className="text-center p-8 text-red-500 bg-red-500/10 rounded-lg">Error: {error}</div>;

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Support Requests</h2>
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Request ID</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedRequests.map((request) => (
            <TableRow key={request.id}>
              <TableCell className="font-mono text-xs">{request.id}</TableCell>
              <TableCell className="font-medium">{request.submittedBy?.name || request.userName}</TableCell>
              <TableCell>{request.subject}</TableCell>
              <TableCell><StatusPill status={request.status as SupportTicketStatus} /></TableCell>
              <TableCell><PriorityPill priority={request.priority as TicketPriority} /></TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                    {request.status !== SupportTicketStatus.CLOSED && (
                        <>
                            <Button variant="outline" size="sm" onClick={() => handleResolve(request.id)}>
                                Resolve
                            </Button>
                            <Button variant="destructive" size="sm" onClick={() => handleEscalate(request.id)}>
                                Escalate
                            </Button>
                        </>
                    )}
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(request.id)}>
                        Delete
                    </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SupportRequestsRealDataTable;
